import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, MessageSquare, RefreshCw, Search } from 'lucide-react';
import {
  closeVendorPolicyQuery,
  fetchVendorPolicyQueries,
  type VendorPolicyQueryRow,
} from '../../services/MasterAdmin/vendorPolicyQueriesService';
import { fetchVendors, processVendorsForDisplay } from '../../services/MasterAdmin/vendorService';

const VendorPolicyQueriesPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<VendorPolicyQueryRow[]>([]);
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [vendorNamesById, setVendorNamesById] = useState<Record<string, string>>({});
  const [closingId, setClosingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchVendorPolicyQueries({
        ...(status ? { status: status as 'OPEN' | 'REVIEWED' | 'CLOSED' } : {}),
        limit: 200,
        offset: 0,
      });
      setRows(response.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load vendor policy queries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const loadVendorNames = async () => {
      try {
        const response = await fetchVendors();
        const vendors = processVendorsForDisplay(response);
        const map: Record<string, string> = {};
        vendors.forEach((vendor) => {
          map[vendor.id] = vendor.name;
        });
        setVendorNamesById(map);
      } catch {
        // Non-blocking: queries page still works with fallback names.
      }
    };
    void loadVendorNames();
  }, []);

  const handleCloseQuery = async (row: VendorPolicyQueryRow) => {
    const confirmed = window.confirm(`Close this query from "${vendorNamesById[row.vendorId] || row.vendorName}"?`);
    if (!confirmed) return;
    try {
      setClosingId(row.queryId);
      await closeVendorPolicyQuery(row.queryId);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to close query');
    } finally {
      setClosingId(null);
    }
  };

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.vendorName,
        row.vendorId,
        row.vendorTypeName,
        row.comment,
        row.vendorDetails?.email,
        row.vendorDetails?.phoneNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [rows, search]);

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Vendor Policy Queries</h1>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            All vendor-submitted policy queries with vendor details for operations review.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Search by vendor, query, email, phone..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.currentTarget.value)}
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          >
            <option value="">All Statuses</option>
            <option value="OPEN">Open</option>
            <option value="REVIEWED">Reviewed</option>
            <option value="CLOSED">Closed</option>
          </select>
          <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center">
            Showing {filteredRows.length.toLocaleString()} queries
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="ma-table-scroll scrollbar-light">
          <table className="w-full min-w-[60rem] divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className="w-[22%] min-w-[12rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Vendor</th>
                <th className="w-[15%] min-w-[9rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Service Type</th>
                <th className="w-[35%] min-w-[16rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Query Details</th>
                <th className="w-[10%] min-w-[7rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</th>
                <th className="w-[18%] min-w-[10rem] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Submitted</th>
                <th className="w-[10%] min-w-[8rem] px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
              {!loading && filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                    No vendor policy queries found.
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.queryId}>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{vendorNamesById[row.vendorId] || row.vendorName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{row.vendorId}</div>
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-300">{row.vendorDetails?.email || '-'}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-300">{row.vendorDetails?.phoneNumber || '-'}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700 dark:text-gray-300">{row.vendorTypeName}</td>
                    <td className="px-4 py-3 align-top text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{row.comment}</td>
                    <td className="px-4 py-3 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          row.status === 'OPEN'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : row.status === 'REVIEWED'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-sm text-gray-600 dark:text-gray-400">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      {row.status !== 'CLOSED' ? (
                        <button
                          type="button"
                          onClick={() => void handleCloseQuery(row)}
                          disabled={closingId === row.queryId}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
                        >
                          <CheckCircle2 className={`h-3.5 w-3.5 ${closingId === row.queryId ? 'animate-pulse' : ''}`} />
                          {closingId === row.queryId ? 'Closing...' : 'Close Query'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Closed</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorPolicyQueriesPage;

